import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import * as Icons from "lucide-react";

export const C2BSoFunktionierts = () => {
  const [refSteps, isStepsVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            So funktioniert's
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            In nur 3 einfachen Schritten zu Ihrem fairen Preis
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt starten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Steps */}
      <section ref={refSteps} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate={isStepsVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="max-w-4xl mx-auto space-y-12"
          >
            {[
              { step: 1, icon: Icons.Search, title: "Gerät auswählen & bewerten", desc: "Wählen Sie Ihr Gerät aus unserer Datenbank und geben Sie den Zustand an. Sie erhalten sofort ein unverbindliches Angebot." },
              { step: 2, icon: Icons.Package, title: "Gerät einsenden", desc: "Nach Annahme des Angebots senden Sie uns Ihr Gerät kostenlos zu. Wir stellen Ihnen ein versichertes Versandlabel zur Verfügung." },
              { step: 3, icon: Icons.Euro, title: "Geld erhalten", desc: "Nach Prüfung Ihres Geräts überweisen wir Ihnen den vereinbarten Betrag innerhalb von 1-2 Werktagen." }
            ].map((item) => (
              <motion.div key={item.step} variants={slideUp}>
                <PremiumCard className="p-8">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="flex-shrink-0"
                    >
                      <div className="w-20 h-20 bg-gradient-to-br from-[#1f7db6] to-[#143e6e] text-white rounded-2xl flex items-center justify-center font-bold text-2xl">
                        {item.step}
                      </div>
                    </motion.div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <item.icon className="text-[#1f7db6]" size={28} />
                        <h3 className="font-bold text-2xl text-[#143e6e]">{item.title}</h3>
                      </div>
                      <p className="text-lg text-gray-700">{item.desc}</p>
                    </div>
                  </div>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1f7db6] via-[#143e6e] to-[#1f7db6]" />
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Bereit anzufangen?
          </motion.h2>
          <motion.p variants={slideUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Starten Sie jetzt und erhalten Sie Ihr Sofortangebot
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Gerät anbieten
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};
